import emailjs from "@emailjs/browser";
import { FC, SyntheticEvent, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import NavbarV2 from "../HomeV2/NavbarV2";
import FooterV2 from "../HomeV2/FooterV2";
import ReCAPTCHA from "react-google-recaptcha";

const SERVICE_OPTIONS = [
  { value: "Sales & Strategy", slug: "sales-strategy" },
  { value: "Business Development", slug: "business-development" },
  { value: "Copy & Content", slug: "copy-content" },
  { value: "Brand & Creative", slug: "brand-creative" },
  { value: "Video Production", slug: "video-production" },
  { value: "Digital Marketing", slug: "digital-marketing" },
  { value: "AI & Tech", slug: "ai-tech" },
  { value: "Build My Team", slug: "build-my-team" },
] as const;

const slugToValue = Object.fromEntries(
  SERVICE_OPTIONS.map((o) => [o.slug, o.value])
);

const ReachOutPage: FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const form = useRef<HTMLFormElement>(null);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [isFailure, setFailure] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidWebsite, setInvalidWebsite] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState("");
  const emailPattern =
    /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

  // Parse URL param ?services=sales-strategy,video-production to pre-select checkboxes
  const servicesParam = searchParams.get("services") || "";
  const preSelectedServices = servicesParam
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter((slug) => slug in slugToValue)
    .map((slug) => slugToValue[slug as keyof typeof slugToValue]);

  const returnPath =
    (location.state as { from?: string })?.from || searchParams.get("returnTo") || "/";

  const sendEmail = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSending || !form.current) return;

    const formData = new FormData(form.current);
    const isBot = formData.get("honeypot");
    if (isBot) return;

    if (!captchaToken) {
      setCaptchaError("Please complete the reCAPTCHA.");
      setTimeout(() => setCaptchaError(""), 1800);
      return;
    }

    const selectedOptions = Array.from(formData.keys())
      .filter((key) => formData.get(key) === "on")
      .map((key) => key);

    const emailData = {
      from_name: formData.get("from_name"),
      form_email: formData.get("form_email"),
      form_tel: formData.get("from_tel"),
      form_comp: formData.get("from_comp"),
      from_website: formData.get("from_website"),
      selectedOptions: selectedOptions.join(", "),
    };

    const emailStr = emailData.form_email?.toString() ?? "";
    if (!emailPattern.test(emailStr)) {
      setInvalidEmail(true);
      setTimeout(() => setInvalidEmail(false), 1800);
      return;
    }

    const websiteStr = (emailData.from_website?.toString() ?? "").trim();
    if (websiteStr && !/^.+\..+$/.test(websiteStr)) {
      setInvalidWebsite(true);
      setTimeout(() => setInvalidWebsite(false), 1800);
      return;
    }

    setIsSending(true);
    setFailure(false);

    const webhookBody = {
      name: emailData.from_name?.toString() ?? "",
      workEmail: emailData.form_email?.toString() ?? "",
      phone: emailData.form_tel?.toString() ?? "",
      company: emailData.form_comp?.toString() ?? "",
      website: emailData.from_website?.toString() ?? "",
      services: selectedOptions.join(", "),
    };

    const sendToWebhook = () =>
      fetch("https://n8n.salesmrkt.com/webhook/9c2d2e44-1bad-4746-a1c1-0b6f2e50aac3", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(webhookBody),
      });

    const trySend = (serviceId: string) =>
      emailjs.send(
        serviceId,
        "template_w2vs322",
        emailData,
        "YX6Ix4FNwXw3hRdaD"
      );

    const onEmailSuccess = () => {
      sendToWebhook();
      setSuccess(true);
      setIsSending(false);
      navigate(returnPath, { replace: true, state: { showThankYou: true } });
    };

    trySend("service_9sbma8h")
      .then(onEmailSuccess)
      .catch(() => {
        trySend("service_eqeh5lk")
          .then(onEmailSuccess)
          .catch(() => {
            setFailure(true);
            setIsSending(false);
            setTimeout(() => setFailure(false), 2000);
          });
      });
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Helmet>
        <title>Reach Out - Salesmrkt</title>
      </Helmet>
      <NavbarV2 />
      <main className="flex-1 pt-[80px] pb-16">
        <div className="max-w-[600px] mx-auto px-6 py-12 flex flex-col items-center">
          <h1 className="text-black text-4xl md:text-5xl font-semibold mb-2 text-center display-inline">
            Reach Out to <span className="text-[#84CC16]">Salesmrkt</span>
          </h1>
          <p className="text-gray-600 text-center mb-10">
            Tell us about your needs and we&apos;ll get back to you soon.
          </p>

          <form
            ref={form}
            onSubmit={sendEmail}
            className="w-full flex flex-col gap-4"
          >
            <input
              type="text"
              name="from_name"
              placeholder="Name"
              required
              className="w-full p-4 border border-gray-300 rounded-lg outline-none focus:border-[#84CC16] transition-colors text-black placeholder:text-gray-400"
            />
            <input
              type="email"
              name="form_email"
              placeholder="Work Email"
              required
              className="w-full p-4 border border-gray-300 rounded-lg outline-none focus:border-[#84CC16] transition-colors text-black placeholder:text-gray-400"
            />
            <input
              type="tel"
              name="from_tel"
              placeholder="Phone"
              required
              inputMode="numeric"
              className="w-full p-4 border border-gray-300 rounded-lg outline-none focus:border-[#84CC16] transition-colors text-black placeholder:text-gray-400"
            />
            <input
              type="text"
              name="from_comp"
              placeholder="Company"
              className="w-full p-4 border border-gray-300 rounded-lg outline-none focus:border-[#84CC16] transition-colors text-black placeholder:text-gray-400"
            />
            <input
              type="text"
              name="from_website"
              placeholder="Website"
              className={`w-full p-4 border rounded-lg outline-none transition-colors text-black placeholder:text-gray-400 ${
                invalidWebsite
                  ? "border-red-400 focus:border-red-400"
                  : "border-gray-300 focus:border-[#84CC16]"
              }`}
            />
            {invalidWebsite && (
              <p className="text-red-500 text-sm -mt-2">Website must contain at least one dot (e.g. example.com)</p>
            )}

            <div className="border border-gray-300 rounded-lg p-4 w-full">
              <h3 className="font-semibold text-black mb-3">
                What services are you researching?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SERVICE_OPTIONS.map((opt) => (
                  <label
                    key={opt.slug}
                    className="flex items-center gap-2 cursor-pointer select-none group"
                  >
                    <input
                      type="checkbox"
                      name={opt.value}
                      className="w-5 h-5 cursor-pointer rounded border-gray-300 text-[#84CC16] focus:ring-[#84CC16]"
                      defaultChecked={preSelectedServices.includes(opt.value)}
                    />
                    <span className="text-sm text-black group-hover:text-[#84CC16] transition-colors">
                      {opt.value}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <input
              type="text"
              name="honeypot"
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />

            <div className="flex justify-center my-2">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={
                  process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY ||
                  "6Let01UrAAAAAHL-taVp8x-NEYGSKQXdw7xh9gPY"
                }
                onChange={(value) => setCaptchaToken(value)}
                size="normal"
              />
            </div>
            {captchaError && (
              <p className="text-red-500 text-sm text-center">{captchaError}</p>
            )}

            <button
              type="submit"
              disabled={isSending}
              className={`w-full py-3 px-6 rounded-lg font-semibold border-2 transition-all duration-300 ${
                isSuccess
                  ? "bg-green-500 border-green-500 text-white"
                  : isFailure
                  ? "bg-red-500 border-red-500 text-white"
                  : invalidEmail || invalidWebsite
                  ? "border-red-400 text-red-600"
                  : "bg-white border-[#84CC16] text-black hover:bg-[#84CC16] hover:text-white hover:ring-2 hover:ring-offset-2 hover:ring-[#84CC16] disabled:hover:ring-0"
              }`}
            >
              {invalidEmail
                ? "Invalid email format"
                : invalidWebsite
                ? "Invalid website format"
                : isSending
                ? "Sending..."
                : isSuccess
                ? "Sent!"
                : isFailure
                ? "Failed to send"
                : "Submit"}
            </button>
          </form>

          <a
            href="https://calendly.com/salesbox-meet/salesmrkt?back=1&month=2025-11"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 w-full py-3 px-6 rounded-lg font-semibold border-2 border-[#84CC16] text-black text-center hover:bg-[#84CC16] hover:text-white hover:ring-2 hover:ring-offset-2 hover:ring-[#84CC16] transition-all duration-300"
          >
            Schedule Meeting
          </a>

          <p className="mt-8 text-gray-500 text-sm flex items-center gap-1">
            <span>📍</span> Seattle – {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </p>
        </div>
      </main>
      <FooterV2 />
    </div>
  );
};

export default ReachOutPage;
